import { listenForMutations, publishMutation } from '@/lib/mutation-broadcast';

// One epoch for Inertia visits and JSON mutations. Inject the router at boot
// so low-level JSON helpers do not need to import React/Inertia themselves.
const STORAGE_KEY = 'bosko:last-mutation-at';

// History entries survive a hard reload (mobile pull-to-refresh) with their
// render stamp, so the last write must too. Per tab, and best effort: merely
// touching sessionStorage can throw in blocked or embedded contexts.
function readLastMutationAt() {
  try {
    const stored = Number(window.sessionStorage.getItem(STORAGE_KEY));
    return Number.isFinite(stored) && stored > 0 ? stored : 0;
  } catch {
    return 0;
  }
}

let activeRouter = null;
let currentPage = null;
let navigationVersion = 0;
const incomingPages = new WeakSet();
const freshnessListeners = new Set();
export const getPageRefreshing = () => Boolean(currentPage?.clientRefreshing);
export function subscribeToPageFreshness(listener) {
  freshnessListeners.add(listener);
  return () => freshnessListeners.delete(listener);
}
const notifyFreshness = () =>
  freshnessListeners.forEach((listener) => listener());
let lastMutationAt = readLastMutationAt();
let lastEpoch = lastMutationAt;

const epoch = () => (lastEpoch = Math.max(Date.now(), lastEpoch + 1));

export const getLastMutationAt = () => lastMutationAt;

export function noteMutation() {
  // Keep ordering even when two writes/rendering happen within one millisecond.
  lastMutationAt = epoch();
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(lastMutationAt));
  } catch {
    // The in-memory epoch still protects this document.
  }
  activeRouter?.flushAll();
  publishMutation(lastMutationAt);
}

// Another tab wrote. Prefetches and history here are now as stale as after a
// write of our own, so the checks below catch them on the next visit or Back.
// The page on screen stays put even so: a reload skips the form guard, and a
// memory review or quiz draws new cards on every request, so it restarts and
// loses a revealed answer no field holds any more, which nothing here can see.
// Never republish: tabs would echo each other.
function receiveMutation(at) {
  if (at <= lastMutationAt) return;
  lastMutationAt = at;
  lastEpoch = Math.max(lastEpoch, at);
  try {
    window.sessionStorage.setItem(STORAGE_KEY, String(at));
  } catch {
    // The in-memory epoch still protects this document.
  }
  activeRouter?.flushAll();
}

// A JSON write may outlive its reader after OS/Android Back. Revalidate the
// destination when that write settles, without resetting the active editor.
export function beginMutation() {
  const startedOn = navigationVersion;
  noteMutation();
  return () => {
    noteMutation();
    if (startedOn !== navigationVersion)
      activeRouter?.reload({ preserveScroll: true, replace: true });
  };
}

export function stampPageRender(page) {
  if (!page) return;
  const incoming = incomingPages.delete(page);
  const restored = !incoming && page.clientRenderedAt != null;
  const partial = incoming && page.props?.partialComponent === page.component;
  if (partial) {
    // Inertia merges only props on partial responses. The server marks the
    // partial component explicitly so concurrent requests cannot be confused.
    page.clientRenderedAt =
      currentPage?.component === page.component
        ? (currentPage.clientRenderedAt ?? 0)
        : 0;
  } else {
    page.clientRenderedAt ??= epoch();
  }
  page.clientRefreshing = Boolean(
    (restored && page.clientRenderedAt < lastMutationAt) ||
    (page.clientPrefetchedAt != null &&
      page.clientPrefetchedAt < lastMutationAt),
  );
  if (
    restored ||
    currentPage?.url !== page.url ||
    currentPage?.component !== page.component
  )
    navigationVersion += 1;
  currentPage = page;
  notifyFreshness();
}

export function installPrefetchInvalidation(router) {
  activeRouter = router;
  let restoring = false;
  const prefetchEpochs = new Map();
  const flushTimers = new Set();
  const mutations = new Map();
  const refreshed = new WeakMap();
  const mutation = (event) => {
    const visit = event.detail.visit;
    if (visit.method.toLowerCase() !== 'get')
      mutations.set(visit.id, beginMutation());
  };
  const refresh = (page) => {
    if (refreshed.get(page) === lastMutationAt) return;
    refreshed.set(page, lastMutationAt);
    page.clientRefreshing = true;
    notifyFreshness();
    router.reload({ preserveScroll: true, replace: true });
  };
  const onPopState = (event) => {
    restoring = Boolean(event.state?.page);
  };
  const onNavigate = (event) => {
    const page = event.detail.page;
    const staleHistory =
      restoring && (page.clientRenderedAt ?? 0) < lastMutationAt;
    const stalePrefetch =
      page.clientPrefetchedAt != null &&
      page.clientPrefetchedAt < lastMutationAt;
    restoring = false;
    currentPage = page;
    if (staleHistory || stalePrefetch || page.clientRefreshing) refresh(page);
  };
  const onPrefetching = (event) => {
    prefetchEpochs.set(event.detail.visit.id, epoch());
  };
  const onPrefetched = (event) => {
    const { visit, response } = event.detail;
    const requestedAt = prefetchEpochs.get(visit.id);
    if (requestedAt == null) return;
    prefetchEpochs.delete(visit.id);
    // The public prefetched event exposes the response before it is cached.
    // Keep its request epoch with the page: a visit can already be waiting on
    // this in-flight response, so flushing alone cannot prevent its rendering.
    try {
      const page =
        typeof response.data === 'string'
          ? JSON.parse(response.data)
          : response.data;
      if (!page?.component || !page.props) return;
      response.data = { ...page, clientPrefetchedAt: requestedAt };
    } catch {
      return; // Leave non-Inertia/invalid responses to Inertia's own handling.
    }
    if (requestedAt < lastMutationAt) {
      // Inertia 3.5 inserts into its cache AFTER prefetched/finish events.
      // A task boundary lets that insertion finish before removing the entry.
      const timer = setTimeout(() => {
        flushTimers.delete(timer);
        router.flushAll();
      }, 0);
      flushTimers.add(timer);
    }
  };
  const onFinish = (event) => {
    const id = event.detail.visit.id;
    mutations.get(id)?.();
    mutations.delete(id);
    prefetchEpochs.delete(id);
  };
  const onVisible = () => {
    if (document.visibilityState === 'visible') router.flushAll();
  };
  const remove = [
    router.on('start', mutation),
    router.on('finish', onFinish),
    router.on('prefetching', onPrefetching),
    router.on('prefetched', onPrefetched),
    router.on('beforeUpdate', (event) => incomingPages.add(event.detail.page)),
    router.on('navigate', onNavigate),
    // Replace visits and handlePrefetch's current-URL render skip navigate.
    router.on('success', onNavigate),
  ];
  const onPageShow = (event) => {
    if (!event.persisted || !currentPage) return;
    lastMutationAt = Math.max(lastMutationAt, readLastMutationAt());
    lastEpoch = Math.max(lastEpoch, lastMutationAt);
    if ((currentPage.clientRenderedAt ?? 0) < lastMutationAt)
      refresh(currentPage);
  };
  window.addEventListener('pageshow', onPageShow);
  // history-interceptor is imported before Inertia. Its stopped overlay,
  // same-page and unsaved-form traversals never reach this listener.
  window.addEventListener('popstate', onPopState);
  document.addEventListener('visibilitychange', onVisible);
  const closeBroadcast = listenForMutations(receiveMutation);
  return () => {
    remove.forEach((unsubscribe) => unsubscribe());
    flushTimers.forEach(clearTimeout);
    window.removeEventListener('popstate', onPopState);
    window.removeEventListener('pageshow', onPageShow);
    document.removeEventListener('visibilitychange', onVisible);
    closeBroadcast();
    if (activeRouter === router) activeRouter = null;
  };
}
